import {CONFIG} from '../../appConfig';
import STATE from '../../constants/state';

const LOCATION = 'location/';
const COUNTRIES = 'countries/';
const COUNTRIES_ISO3 = 'countries-sorted-by-name';
const STATES_OR_PROVINCES = 'statesorprovinces';
const STATES_OR_PROVINCES_ISO3 = 'stateAndProvince';
const BRANCH_POLICIES = 'branchPolicies';
const COUNTRIES_PCIR = 'countriesPcir';

export default {
  /**
   * @desc Get location information by ID
   * @param {Object} data {endpoint, params}
   * @returns {Object}
   * @memberof services.gma.location
   * @example
   * const data = {
   *  endpoint: {
   *    id, // *(String)* i.e. 'ORDT61',
   *  },
   *  params: {
   *    policesFormattedText: true // *(Boolean)*
   *  }
   * }
   * services.gma.location.byId(data);
   * // dispatched results stored in `state.gmi.gma.by_id[id]`
   */
  byId(data = {}){
    const {endpoint = {}, params = {}} = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: LOCATION + endpoint.id,
      params,
      statePath: STATE.GMA.LOCATION.BY_ID.concat([endpoint.id])
    };
  },

  /**
   * @desc Get list of all countries
   * @returns {Object} data {params, endpoint}
   * @memberof services.gma.location
   * @example
   * const data = {
   *  endpoint: {
   *    iso3 true // *(Boolean)* Request ISO3 codes, rather than default ISO2 codes
   *  }
   * }
   * services.gma.location.countries();
   * // dispatched results stored in `state.gmi.gma.location.countries`
   */
  countries(data = {}){
    const {params = {}, endpoint = {}} = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: LOCATION + (endpoint.iso3 ? COUNTRIES_ISO3 :COUNTRIES),
      params,
      statePath: STATE.GMA.LOCATION.COUNTRIES
    };
  },

  /**
   * @desc Get list of states or provinces for a specific country
   * @param {Object} data {endpoint, params}
   * @returns {Object}
   * @memberof services.gma.location
   * @example
   * const data = {
   *  endpoint: {
   *    countryCode // *(String)* i.e. 'US',
   *    iso3 true // *(Boolean)* Is the country code being passed in ISO3 format?
   *  }
   * }
   * services.gma.location.statesOrProvinces(data);
   * // dispatched results stored in `state.gmi.gma.location.states_or_provinces`
   */
  statesOrProvinces(data = {}){
    const {endpoint = {}, params = {}} = data;
    const ENDPOINT_SUFFIX = endpoint.iso3 ? STATES_OR_PROVINCES_ISO3 : STATES_OR_PROVINCES;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: LOCATION + COUNTRIES + endpoint.countryCode + '/' + ENDPOINT_SUFFIX,
      params,
      statePath: STATE.GMA.LOCATION.STATES_OR_PROVINCES.concat([endpoint.countryCode])
    };
  },

  /**
   * @desc Get list of branch policies for a specific country
   * @param {Object} data {endpoint, params}
   * @returns {Object}
   * @memberof services.gma.location
   * @example
   * const data = {
   *  endpoint: {
   *    countryCode // *(String)* i.e. 'US'
   *  }
   * }
   * services.gma.location.branchPolicies(data);
   * // dispatched results stored in `state.gmi.gma.location.branch_policies`
   */
  branchPolicies(data = {}){
    const {endpoint = {}, params = {}} = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: LOCATION + COUNTRIES + endpoint.countryCode + '/' + BRANCH_POLICIES,
      params,
      statePath: STATE.GMA.LOCATION.BRANCH_POLICIES.concat([endpoint.countryCode])
    };
  },

  /**
   * @desc Get list of countries for PCIR flow
   * @returns {Object}
   * @memberof services.gma.location
   * @example
   * services.gma.location.countriesPcir;
   * // dispatched results stored in `state.gmi.gma.location.countriesPcir`
   */
  countriesPcir(){
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: LOCATION + COUNTRIES_PCIR,
      statePath: STATE.GMA.LOCATION.COUNTRIES
    };
  }
};
